import { useCallback } from 'react';
import { Layer } from 'shared/map-exports';
import {
  EvacZoneStatus,
  GeoEventEvacZoneStatus,
  RegionEvacZoneStyle,
} from 'shared/types';
import { Poi, EvacZonePoi } from 'features/mapping/mapping.types';
import { usePoisState } from 'state/usePoisState';
import { useMapLayersState } from 'state/useMapLayersState';
import { useActiveEvacZonesQuery } from 'features/evacZones/hooks/useActiveEvacZonesQuery';
import useAppInfo from 'hooks/useAppInfo';
import VisibilityToggledSource from '../VisibilityToggledSource';
import {
  FEATURE_UID_V2_FIELD_NAME,
  ZONES_SOURCE_LAYER,
  EVAC_ZONES_SOURCE_LAYER_NAME,
  SOURCE_ID,
} from './constants';
import { getSelectedEvacZoneUId } from './utils';
import {
  getFillStyleAll,
  getLabelStyleAll,
  getSelectedZonePatternStyle,
  getSelectedZoneStrokeStyle,
  getStrokeStyleAll,
} from './layerStyles';
import { MapboxFeature, useMapLayerEvents } from '../useMapLayerEvents';
import { EvacZoneStyles } from '../../../../constants';
import { getWatchdutyTileUrl } from '../getWatchdutyTileUrl';

type StructuredEvacuationsViewLayerProps = {
  visible: boolean;
};

type AllEvacZonesProperties = {
  display_name: string;
  source_attribution: string;
  uid_v2: string;
  status?: EvacZoneStatus | '';
  evac_zone_style: RegionEvacZoneStyle;
  region_id: number;
  date_created: string;
  date_modified: string;
};

const REFRESH_INTERVAL_SECONDS = 3600; // 1 hour
const VIEW_SOURCE_ID = `${SOURCE_ID}_ALL`;
const FILL_LAYER_ID = 'fill_layer_all';

const handleClickEvent = (
  features: MapboxFeature<AllEvacZonesProperties>[],
  setSelectedPoi: (poi: Poi) => void,
  activeEvacZones: GeoEventEvacZoneStatus[],
): void => {
  for (const feature of features) {
    if (feature.properties) {
      const activeEvacZone = activeEvacZones.find(
        (activeZone) => activeZone.evacZone.uidV2 === feature.properties.uid_v2,
      );

      const poiDialogData: EvacZonePoi = {
        uid: feature.properties.uid_v2,
        displayName: feature.properties.display_name,
        status: feature.properties.status || undefined,
        dateCreated:
          activeEvacZone?.dateCreated || feature.properties.date_created,
        dateModified:
          activeEvacZone?.dateModified || feature.properties.date_modified,
        source: feature.properties.source_attribution,
        style: feature.properties.evac_zone_style || EvacZoneStyles.default,
      };
      setSelectedPoi({
        type: 'evacZone',
        id: feature.properties.uid_v2,
        metadata: poiDialogData,
      });
    }
  }
};

/*
 * This layer is designed to show all pre-planned zones available with no filtering directly from the tile feature
 * properties with no affiliation to our backend evac zone data and/or statuses.
 */
const StructuredEvacuationsAllLayer = (
  props: StructuredEvacuationsViewLayerProps,
): JSX.Element => {
  const { visible } = props;
  const { selectedPoi, setSelectedPoi } = usePoisState();
  const { isDarkBaseLayer } = useMapLayersState();
  const { appVersion } = useAppInfo();
  const activeEvacZones = useActiveEvacZonesQuery();

  const clickCallback = useCallback(
    (features: MapboxFeature<AllEvacZonesProperties>[]) =>
      handleClickEvent(features, setSelectedPoi, activeEvacZones),
    [setSelectedPoi, activeEvacZones],
  );

  // Not clear why - but the all view is receiving click events before the layers that are rendered above it.
  //   so we need to allow the event propagation to occur on the click.
  useMapLayerEvents<AllEvacZonesProperties>({
    layerId: FILL_LAYER_ID,
    onClick: clickCallback,
  });

  const selectedEvacZoneUId = getSelectedEvacZoneUId(selectedPoi);

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl(EVAC_ZONES_SOURCE_LAYER_NAME, {
        app_version: appVersion,
      })}
      visible={visible}
      promoteId={FEATURE_UID_V2_FIELD_NAME}
      // Do not unset `volatile` ever for this layer.
      volatile
      id={VIEW_SOURCE_ID}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer id={FILL_LAYER_ID} {...getFillStyleAll()} />
      <Layer id="stroke_layer_all" {...getStrokeStyleAll(isDarkBaseLayer)} />

      <Layer
        id="selected_pattern_all"
        {...getSelectedZonePatternStyle(
          selectedEvacZoneUId,
          ZONES_SOURCE_LAYER,
          FEATURE_UID_V2_FIELD_NAME,
        )}
      />
      <Layer
        id="selected_stroke_all"
        {...getSelectedZoneStrokeStyle(
          selectedEvacZoneUId,
          ZONES_SOURCE_LAYER,
          FEATURE_UID_V2_FIELD_NAME,
        )}
      />
      <Layer id="zones_label_all" {...getLabelStyleAll()} />
    </VisibilityToggledSource>
  );
};

export default StructuredEvacuationsAllLayer;
